<template>
  <div class="full-calendar" :class="[`mode-${mode}`, numberOfMonths > 1 ? 'multi-month' : '']" dir="rtl">
    <!-- Header for single month -->
    <div v-if="numberOfMonths === 1" class="flex items-center justify-between" :class="mode === 'compact' ? 'mb-2' : 'mb-4'">
      <h2 :class="mode === 'compact' ? 'text-base font-medium' : 'text-lg font-medium'">
        {{ getMonthName(viewDate) }} {{ viewDate.getFullYear() }}
      </h2>
      <div class="flex items-center gap-2">
        <span v-wave :class="mode === 'compact' ? 'rounded-full px-3 py-0.5 text-sm border border-gray-300 cursor-pointer' : 'rounded-full px-4 py-1 border border-gray-300 cursor-pointer'" @click="goToToday">
          {{ todayLabel }}
        </span>
        <Button
          icon="pi pi-chevron-right"
          text
          rounded
          :class="mode === 'compact' ? '!text-black !w-8 !h-8' : '!text-black'"
          @click="previousMonth"
        />
        <Button
          icon="pi pi-chevron-left"
          text
          rounded
          :class="mode === 'compact' ? '!text-black !w-8 !h-8' : '!text-black'"
          @click="nextMonth"
        />
      </div>
    </div>

    <!-- Header for multi-month -->
    <div v-else class="flex items-center justify-between" :class="mode === 'compact' ? 'mb-2' : 'mb-4'">
      <div class="flex items-center gap-2">
        <span v-wave :class="mode === 'compact' ? 'rounded-full px-3 py-0.5 text-sm border border-gray-300 cursor-pointer' : 'rounded-full px-4 py-1 border border-gray-300 cursor-pointer'" @click="goToToday">
          {{ todayLabel }}
        </span>
        <Button
          icon="pi pi-chevron-right"
          text
          rounded
          :class="mode === 'compact' ? '!text-black !w-8 !h-8' : '!text-black'"
          @click="previousMonth"
        />
        <Button
          icon="pi pi-chevron-left"
          text
          rounded
          :class="mode === 'compact' ? '!text-black !w-8 !h-8' : '!text-black'"
          @click="nextMonth"
        />
      </div>
    </div>

    <!-- Calendar Views Container -->
    <div class="calendars-container" :class="numberOfMonths > 1 ? 'multi' : ''">
      <div
        v-for="monthOffset in numberOfMonths"
        :key="monthOffset"
        class="calendar-view"
      >
        <!-- Month header for multi-month view -->
        <div v-if="numberOfMonths > 1" class="month-header" :class="mode === 'compact' ? 'mb-2' : 'mb-3'">
          <h3 :class="mode === 'compact' ? 'text-sm font-medium text-center' : 'text-base font-medium text-center'">
            {{ getMonthName(getMonthDate(monthOffset - 1)) }} {{ getMonthDate(monthOffset - 1).getFullYear() }}
          </h3>
        </div>

        <!-- Weekday Headers -->
        <div class="weekday-headers" :class="mode === 'compact' ? 'mb-2' : 'mb-4'">
          <div
            v-for="day in weekDayLabels"
            :key="day"
            class="weekday-header"
          >
            {{ day }}
          </div>
        </div>

        <!-- Calendar Grid -->
        <div class="calendar-grid" :class="mode === 'compact' ? 'gap-1' : 'gap-2'">
          <div
            v-for="day in getCalendarDays(monthOffset - 1)"
            :key="day.date?.toISOString() || `empty-${monthOffset}-${day.index}`"
            class="calendar-day"
            :class="[
              {
                'empty': !day.date,
                'selected': day.date && isSelected(day.date),
                'in-range': day.date && isInRange(day.date),
                'range-start': day.date && isRangeStart(day.date),
                'range-end': day.date && isRangeEnd(day.date),
                'disabled': day.date && day.disabled,
                'inactive': day.date && !isSelected(day.date) && !isInRange(day.date) && !day.disabled,
                'other-month': day.date && !isSameMonth(day.date, monthOffset - 1)
              },
              mode === 'compact' ? 'compact' : 'default'
            ]"
            @click="day.date && handleDateClick(day.date)"
            @mouseenter="day.date && handleDateHover(day.date)"
          >
            <span v-if="day.date">{{ day.date?.getDate() }}</span>
            <span v-if="day.date && hasEvent(day.date)" class="event-dot"></span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Button from 'primevue/button';

interface CalendarDay {
  date: Date | null;
  disabled: boolean;
  index: number;
}

interface CalendarEvent {
  date: Date;
}

type DateRange = [Date | null, Date | null];

const props = withDefaults(defineProps<{
  modelValue?: Date | DateRange;
  selectionMode?: 'single' | 'range';
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Date[];
  primaryColor?: string;
  primaryFontColor?: string;
  mode?: 'default' | 'compact';
  numberOfMonths?: number;
  itemsBg?: string;
  events?: CalendarEvent[];
  weekDayLabels?: string[];
  monthLabels?: string[];
  todayLabel?: string;
}>(), {
  selectionMode: 'single',
  disabledDates: () => [],
  primaryColor: '#000000',
  primaryFontColor: '#ffffff',
  mode: 'default',
  numberOfMonths: 1,
  events: () => [],
  weekDayLabels: () => ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'],
  monthLabels: () => ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'],
  todayLabel: 'היום',
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: Date | DateRange): void;
  (e: 'month-change', value: Date): void;
}>();

const isRangeMode = computed(() => props.selectionMode === 'range');

const initialSingleDate = (): Date => {
  if (props.selectionMode === 'single' && props.modelValue instanceof Date) {
    return props.modelValue;
  }
  if (props.selectionMode === 'range' && Array.isArray(props.modelValue) && props.modelValue[0]) {
    return props.modelValue[0];
  }
  return new Date();
};

const initialRange = (): DateRange => {
  if (props.selectionMode === 'range' && Array.isArray(props.modelValue)) {
    return [props.modelValue[0] ?? null, props.modelValue[1] ?? null];
  }
  return [null, null];
};

const selectedDate = ref<Date>(initialSingleDate());
const rangeStart = ref<Date | null>(initialRange()[0]);
const rangeEnd = ref<Date | null>(initialRange()[1]);
const hoverDate = ref<Date | null>(null);
const viewDate = ref<Date>(new Date(rangeStart.value ?? selectedDate.value));
const itemsBgColor = props.itemsBg ?? '#f5f5f5';

// Get month name from a date
const getMonthName = (date: Date): string => {
  return props.monthLabels[date.getMonth()];
};

// Get date for a specific month offset
const getMonthDate = (offset: number): Date => {
  return new Date(viewDate.value.getFullYear(), viewDate.value.getMonth() + offset, 1);
};

// Generate calendar grid for a specific month offset
const getCalendarDays = (monthOffset: number): CalendarDay[] => {
  const targetDate = getMonthDate(monthOffset);
  const year = targetDate.getFullYear();
  const month = targetDate.getMonth();

  // First day of the month
  const firstDay = new Date(year, month, 1);
  const firstDayOfWeek = firstDay.getDay();

  // Last day of the month
  const lastDay = new Date(year, month + 1, 0);

  const days: CalendarDay[] = [];
  let dayIndex = 0;

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfWeek; i++) {
    days.push({ date: null, disabled: false, index: dayIndex++ });
  }

  // Add all days of the month
  for (let day = 1; day <= lastDay.getDate(); day++) {
    const date = new Date(year, month, day);
    days.push({
      date,
      disabled: isDateDisabled(date),
      index: dayIndex++
    });
  }

  // Add empty cells for days after the last day of the month
  const remainingCells = 7 - ((days.length) % 7);
  if (remainingCells < 7) {
    for (let i = 0; i < remainingCells; i++) {
      days.push({ date: null, disabled: false, index: dayIndex++ });
    }
  }

  return days;
};

const isDateDisabled = (date: Date): boolean => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);

  // Check if date is in disabled dates array
  if (props.disabledDates.some(d => {
    const nd = new Date(d);
    nd.setHours(0, 0, 0, 0);
    return nd.getTime() === normalizedDate.getTime();
  })) {
    return true;
  }

  // Check if date is before minDate
  if (props.minDate) {
    const minDate = new Date(props.minDate);
    minDate.setHours(0, 0, 0, 0);
    if (normalizedDate < minDate) {
      return true;
    }
  }

  // Check if date is after maxDate
  if (props.maxDate) {
    const maxDate = new Date(props.maxDate);
    maxDate.setHours(0, 0, 0, 0);
    if (normalizedDate > maxDate) {
      return true;
    }
  }

  return false;
};

const sameDay = (a: Date | null, b: Date | null): boolean => {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const isSelected = (date: Date): boolean => {
  if (isRangeMode.value) {
    return sameDay(date, rangeStart.value) || sameDay(date, rangeEnd.value);
  }
  return sameDay(date, selectedDate.value);
};

const isRangeStart = (date: Date): boolean => isRangeMode.value && sameDay(date, rangeStart.value);
const isRangeEnd = (date: Date): boolean => isRangeMode.value && sameDay(date, rangeEnd.value);

const isInRange = (date: Date): boolean => {
  if (!isRangeMode.value || !rangeStart.value) return false;
  const end = rangeEnd.value ?? hoverDate.value;
  if (!end) return false;
  const d = new Date(date); d.setHours(0, 0, 0, 0);
  const s = new Date(rangeStart.value); s.setHours(0, 0, 0, 0);
  const e = new Date(end); e.setHours(0, 0, 0, 0);
  const lo = s <= e ? s : e;
  const hi = s <= e ? e : s;
  return d >= lo && d <= hi;
};

const isSameMonth = (date: Date, monthOffset: number = 0): boolean => {
  const targetDate = getMonthDate(monthOffset);
  return date.getMonth() === targetDate.getMonth() &&
    date.getFullYear() === targetDate.getFullYear();
};

const handleDateClick = (date: Date) => {
  if (isDateDisabled(date)) return;

  if (isRangeMode.value) {
    if (!rangeStart.value || (rangeStart.value && rangeEnd.value)) {
      rangeStart.value = new Date(date);
      rangeEnd.value = null;
      hoverDate.value = null;
    } else {
      let start = rangeStart.value;
      let end = new Date(date);
      if (end < start) [start, end] = [end, start];
      rangeStart.value = start;
      rangeEnd.value = end;
    }
    emit('update:modelValue', [rangeStart.value, rangeEnd.value]);
    return;
  }

  selectedDate.value = new Date(date);
  emit('update:modelValue', new Date(date));
};

const handleDateHover = (date: Date) => {
  if (!isRangeMode.value) return;
  if (!rangeStart.value || rangeEnd.value) return;
  hoverDate.value = date;
};

const previousMonth = () => {
  viewDate.value = new Date(viewDate.value.getFullYear(), viewDate.value.getMonth() - 1, 1);
  emit('month-change', new Date(viewDate.value));
};

const nextMonth = () => {
  viewDate.value = new Date(viewDate.value.getFullYear(), viewDate.value.getMonth() + 1, 1);
  emit('month-change', new Date(viewDate.value));
};

const goToToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  viewDate.value = new Date(today);
  if (isRangeMode.value) return;
  selectedDate.value = new Date(today);
  emit('update:modelValue', new Date(today));
};

const eventDates = computed<Set<string>>(() => {
  const s = new Set<string>();
  for (const event of props.events) {
    const d = new Date(event.date);
    d.setHours(0, 0, 0, 0);
    s.add(d.toDateString());
  }
  return s;
});

const hasEvent = (date: Date): boolean => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return eventDates.value.has(d.toDateString());
};

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
  if (!newValue) return;
  if (isRangeMode.value && Array.isArray(newValue)) {
    rangeStart.value = newValue[0] ?? null;
    rangeEnd.value = newValue[1] ?? null;
    if (rangeStart.value) viewDate.value = new Date(rangeStart.value);
  } else if (!isRangeMode.value && newValue instanceof Date) {
    selectedDate.value = new Date(newValue);
    viewDate.value = new Date(newValue);
  }
});
</script>

<style scoped lang="scss">
.full-calendar {
  --primary-color: v-bind(primaryColor);
  --primary-font-color: v-bind(primaryFontColor);

  @apply bg-white rounded-lg;
}

/* Calendars Container */
.calendars-container {
  @apply flex w-full;

  &.multi {
    @apply gap-8;
  }

  .calendar-view {
    flex: 1;
  }
}

/* Calendar View Styles */
.calendar-view {
  @apply overflow-hidden;
}

.month-header {
  @apply text-gray-700;
}

.weekday-headers {
  @apply grid grid-cols-7 place-items-center mb-4;
}

.weekday-header {
  @apply font-medium text-gray-500 text-center;

  .mode-default & {
    @apply text-sm;
    width: 48px;
  }

  .mode-compact & {
    @apply text-xs;
    width: 36px;
  }
}

.calendar-grid {
  @apply grid grid-cols-7 place-items-center;

  .mode-default & {
    @apply gap-y-2;
  }

  .mode-compact & {
    @apply gap-y-1;
  }
}

.calendar-day {
  @apply relative flex items-center justify-center rounded-full text-gray-900;
  cursor: pointer;
  transition: background-color 0.2s ease;
  aspect-ratio: 1;

  &.in-range:not(.range-start):not(.range-end) {
    background-color: color-mix(in srgb, var(--primary-color) 18%, transparent);
    color: var(--primary-color);
  }

  // Default mode (original size)
  &.default {
    width: 48px;
    @apply text-base;
  }

  // Compact mode (smaller)
  &.compact {
    width: 36px;
    @apply text-sm;
  }

  .event-dot {
    position: absolute;
    bottom: 3px;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: var(--primary-color);
  }

  &.compact .event-dot {
    width: 3px;
    height: 3px;
    bottom: 2px;
  }

  &.selected .event-dot {
    background-color: var(--primary-font-color);
  }

  &.empty {
    @apply cursor-default;
  }

  &.other-month {
    @apply text-gray-400;
  }

  &.inactive {
    background-color: v-bind(itemsBgColor);

    &:hover {
      background-color: #ececec;
    }
  }

  &.selected {
    @apply font-medium;
    background-color: var(--primary-color);
    color: var(--primary-font-color);

    // Default mode - diagonal stripes for disabled
    &.disabled.default {
      @apply opacity-50;
      background-image: repeating-linear-gradient(
          45deg,
          #f5f5f5 0px,
          #f5f5f5 3px,
          var(--primary-color) 3px,
          var(--primary-color) 6px
      ) !important;
      background-color: transparent !important;
      cursor: pointer;
    }

    // Compact mode - simple line-through for disabled
    &.disabled.compact {
      @apply opacity-70;
      position: relative;
      cursor: pointer;

      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 10%;
        right: 10%;
        height: 1.5px;
        background-color: var(--primary-font-color);
        transform: translateY(-50%);
      }
    }
  }

  &.disabled {
    // Default mode - diagonal stripes
    &.default {
      @apply opacity-50;
      background-image: repeating-linear-gradient(
          45deg,
          #f5f5f5 0px,
          #f5f5f5 3px,
          #e0e0e0 3px,
          #e0e0e0 6px
      ) !important;
      background-color: transparent !important;
      cursor: pointer;

      &:hover {
        background-image: repeating-linear-gradient(
            45deg,
            #f5f5f5 0px,
            #f5f5f5 3px,
            #e0e0e0 3px,
            #e0e0e0 6px
        ) !important;
        opacity: 0.6;
      }
    }

    // Compact mode - simple opacity and line-through
    &.compact {
      @apply opacity-40;
      position: relative;
      cursor: pointer;
      background-color: #f5f5f5;

      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 15%;
        right: 15%;
        height: 1px;
        background-color: #9ca3af;
        transform: translateY(-50%);
      }

      &:hover {
        opacity: 0.5;
      }
    }
  }
}
</style>
